import { useRef, useState } from 'react'
import { Download, FileSpreadsheet, Loader2, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { downloadBinaryFile } from '@/lib/csv'
import { useTenant } from '@/providers/TenantProvider'
import { resourceCapabilitiesApi } from '@/api/resourceCapabilitiesApi'
import {
  buildQualificationTemplate,
  parseQualificationWorkbook,
  toImportPayload,
  type ParsedQualificationFile,
} from '@/lib/qualificationExcel'
import type {
  QualificationImportPlan,
  QualificationImportResult,
} from '@/api/resourceCapabilitiesApi'

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

/**
 * Assign qualifications in bulk from a workbook.
 *
 * Three steps, deliberately: download what is stored, edit it, upload it back.
 * The download is pre-filled with the current matrix and carries dropdowns of
 * the valid people, methods and procedures — so most row errors never happen,
 * and changing three assignments does not mean retyping a hundred.
 *
 * MERGE only, and the dialog says so where it matters. A row absent from the
 * file is left alone: qualification decides who an assignee picker offers, and
 * a method losing its last holder would leave every step it covers assignable
 * to nobody. One forgotten row in a spreadsheet must not be able to do that.
 */
export function QualificationImportDialog({
  open,
  onOpenChange,
  onImported,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Refresh the grids — an applied file changes rows they are showing. */
  onImported: () => void
}) {
  const { selectedTenantId: tenantId } = useTenant()
  const fileInput = useRef<HTMLInputElement>(null)

  const [busy, setBusy] = useState<'template' | 'preview' | 'commit' | null>(null)
  /** What the downloaded file ended up containing, so the empty case is stated. */
  const [downloaded, setDownloaded] = useState<{ rows: number; choices: number } | null>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [parsed, setParsed] = useState<ParsedQualificationFile | null>(null)
  const [plan, setPlan] = useState<QualificationImportPlan | null>(null)
  const [applied, setApplied] = useState<QualificationImportResult['applied'] | null>(null)
  const [error, setError] = useState<string | null>(null)

  function reset() {
    setDownloaded(null)
    setFileName(null)
    setParsed(null)
    setPlan(null)
    setApplied(null)
    setError(null)
  }

  /** The server's `{ errors: string[] }` body, or whatever it managed to say. */
  function messageOf(e: unknown): string {
    if (e && typeof e === 'object' && 'errors' in e) {
      const errors = (e as { errors?: unknown }).errors
      if (Array.isArray(errors) && errors.length > 0) return errors.map(String).join(' ')
    }
    return e instanceof Error ? e.message : 'Something went wrong.'
  }

  async function downloadTemplate() {
    if (!tenantId) return
    setBusy('template')
    setError(null)
    try {
      const template = await resourceCapabilitiesApi.importTemplate(tenantId)
      const buffer = await buildQualificationTemplate(template)
      downloadBinaryFile('qualifications.xlsx', buffer, XLSX_MIME)
      // What the file actually contains, so an empty one is not mistaken for a
      // failed download.
      setDownloaded({
        rows: template.assignments.procedures.length + template.assignments.methods.length,
        choices: template.methods.length + template.procedures.length,
      })
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(null)
    }
  }

  async function chooseFile(file: File) {
    if (!tenantId) return
    reset()
    setFileName(file.name)
    setBusy('preview')
    try {
      const read = await parseQualificationWorkbook(await file.arrayBuffer())
      setParsed(read)
      const payload = toImportPayload(read)
      if (!payload.procedures && !payload.methods) {
        setError(
          read.errors[0] ??
            read.procedures?.errors[0] ??
            read.methods?.errors[0] ??
            'That file has no rows to import.',
        )
        return
      }
      setPlan(await resourceCapabilitiesApi.importPreview(tenantId, payload))
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(null)
    }
  }

  async function apply() {
    if (!tenantId || !parsed) return
    setBusy('commit')
    setError(null)
    try {
      const result = await resourceCapabilitiesApi.importCommit(tenantId, toImportPayload(parsed))
      setPlan(result)
      setApplied(result.applied)
      onImported()
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(null)
    }
  }

  const applicable = plan ? plan.counts.create + plan.counts.update : 0
  const rows = plan ? [...plan.procedures, ...plan.methods] : []

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next) reset()
        onOpenChange(next)
      }}
    >
      <DialogContent className='sm:max-w-2xl'>
        <DialogHeader>
          <DialogTitle>Assign qualifications from a spreadsheet</DialogTitle>
          <DialogDescription>
            Pre-filled with what is already assigned. Uploading only adds and updates.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4'>
          <div className='flex flex-wrap items-center gap-2'>
            <Button variant='outline' size='sm' onClick={() => void downloadTemplate()} disabled={busy !== null}>
              {busy === 'template' ? (
                <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />
              ) : (
                <Download className='mr-1.5 h-3.5 w-3.5' />
              )}
              Download template
            </Button>
            <Button size='sm' onClick={() => fileInput.current?.click()} disabled={busy !== null}>
              {busy === 'preview' ? (
                <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />
              ) : (
                <Upload className='mr-1.5 h-3.5 w-3.5' />
              )}
              Choose a file
            </Button>
            <input
              ref={fileInput}
              type='file'
              accept='.xlsx'
              className='hidden'
              onChange={e => {
                const file = e.target.files?.[0]
                // Cleared so choosing the SAME file again re-runs the preview,
                // which is what someone does after fixing a row.
                e.target.value = ''
                if (file) void chooseFile(file)
              }}
            />
            {fileName && (
              <span className='flex items-center gap-1.5 text-xs text-muted-foreground'>
                <FileSpreadsheet className='h-3.5 w-3.5' />
                {fileName}
              </span>
            )}
          </div>

          {error && <p className='text-xs font-medium text-destructive'>{error}</p>}

          {downloaded && (
            <p className='text-xs text-muted-foreground'>
              {downloaded.rows > 0
                ? `${downloaded.rows} row${downloaded.rows === 1 ? '' : 's'} filled in.`
                : downloaded.choices > 0
                  ? 'Empty — fill in the rows and upload.'
                  : // Nothing to be qualified IN yet, so the file is a dead end.
                    'No methods or procedures yet. Create a method first.'}
            </p>
          )}

          {plan?.sheetErrors.map(message => (
            <p key={message} className='text-xs font-medium text-destructive'>
              {message}
            </p>
          ))}

          {plan && (
            <div className='space-y-3'>
              <div className='flex flex-wrap gap-2 text-xs'>
                <Count label='New' value={plan.counts.create} tone='add' />
                <Count label='Updated' value={plan.counts.update} tone='change' />
                {/* Its own count, not folded into "updated": a file that
                    changes nothing should look like it changes nothing. */}
                <Count label='Already correct' value={plan.counts.noop} tone='muted' />
                <Count label='Errors' value={plan.counts.errors} tone='error' />
              </div>

              {rows.length > 0 && (
                <div className='max-h-56 overflow-y-auto rounded-md border'>
                  <table className='w-full text-xs'>
                    <tbody>
                      {rows.map(row => (
                        <tr key={`${row.sheet}-${row.rowNumber}`} className='border-b last:border-0'>
                          <td className='w-16 px-2 py-1 text-muted-foreground tabular-nums'>
                            {/* The row number as Excel shows it, so a person
                                can go straight to it. */}
                            {row.sheet === 'methods' ? 'M' : 'P'}
                            {row.rowNumber}
                          </td>
                          <td className='px-2 py-1'>{row.personLabel}</td>
                          <td className='px-2 py-1'>
                            {'methodLabel' in row ? row.methodLabel : row.procedureLabel}
                            {'preference' in row && row.preference !== undefined && (
                              <span className='ml-1.5 text-muted-foreground'>
                                pref {row.preference}, {row.proficiency}%
                              </span>
                            )}
                          </td>
                          <td className='w-28 px-2 py-1 text-right text-muted-foreground'>
                            {row.action === 'noop'
                              ? 'no change'
                              : row.action === 'create'
                                ? 'new'
                                : `changes ${'changed' in row ? row.changed.join(', ') : ''}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {plan.errors.length > 0 && (
                <div className='max-h-40 space-y-1 overflow-y-auto rounded-md border border-destructive/40 p-2'>
                  {plan.errors.map(e => (
                    <p key={`${e.sheet}-${e.rowNumber}-${e.message}`} className='text-xs text-destructive'>
                      <span className='font-semibold tabular-nums'>
                        {e.sheet === 'methods' ? 'Methods' : 'Procedures'} row {e.rowNumber}:
                      </span>{' '}
                      {e.message}
                    </p>
                  ))}
                </div>
              )}

              {plan.warnings.length > 0 && (
                <div className='space-y-1'>
                  {plan.warnings.map(w => (
                    <p key={`${w.sheet}-${w.rowNumber}-${w.message}`} className='text-xs text-amber-600'>
                      {w.message}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {applied && (
            <p className='text-xs font-medium text-emerald-600'>
              Applied {applied.methods} method{applied.methods === 1 ? '' : 's'} and{' '}
              {applied.procedures} procedure{applied.procedures === 1 ? '' : 's'}.
            </p>
          )}
        </div>

        <div className='flex items-center justify-end gap-2'>
          <Button variant='ghost' size='sm' onClick={() => onOpenChange(false)}>
            {applied ? 'Close' : 'Cancel'}
          </Button>
          <Button
            size='sm'
            onClick={() => void apply()}
            // Disabled after a successful apply: the same file re-applied is a
            // no-op, and offering the button invites wondering why nothing
            // happened the second time.
            disabled={busy !== null || applicable === 0 || applied !== null}
          >
            {busy === 'commit' && <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />}
            {applicable === 0 ? 'Nothing to apply' : `Apply ${applicable} row${applicable === 1 ? '' : 's'}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function Count({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone: 'add' | 'change' | 'muted' | 'error'
}) {
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 font-medium tabular-nums',
        tone === 'add' && 'bg-emerald-50 text-emerald-700',
        tone === 'change' && 'bg-blue-50 text-blue-700',
        tone === 'muted' && 'bg-muted text-muted-foreground',
        // Never dressed up as neutral, even at zero — an error count is the
        // thing a person most needs to notice.
        tone === 'error' && (value > 0 ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground'),
      )}
    >
      {label} {value}
    </span>
  )
}
